import StartupPageTitle from '../components/StartupPageTitle';
import { CreditCard as PageHeadingIcon } from '@phosphor-icons/react';
import React, { useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { formatDate } from '@/utils/datetime';
import { CreditCard, Clock, Sparkle, Warning, CheckCircle, Trash, Tag, X, Receipt, DownloadSimple, FileText } from '@phosphor-icons/react';
import { useCurrency } from '@/hooks/useCurrency';
import { payWithCashfree, verifyCashfreeReturn, cashfreeNotPaidMessage, CashfreePayButton } from '@/components/CashfreeCheckout';
import CoinsWallet, { CoinsBalanceChip } from '@/components/CoinsWallet';
import { useModuleAccess } from '@/moduleAccess';

const Billing = ({ user, hideHeader = false }) => {
  const { format } = useCurrency();
  const { isEnabled } = useModuleAccess();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCardForm, setShowCardForm] = useState(false);
  // Lifted coupon state so CouponSection + MarketplaceCharges share one source of truth.
  const [savedCoupon, setSavedCoupon] = useState(null);
  const [savedCouponLoaded, setSavedCouponLoaded] = useState(false);
  // Founderboard Coins (startups only) — one fetch feeds the header chip and the section.
  const [coinWallet, setCoinWallet] = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/billing/status');
      setStatus(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load billing'); }
    finally { setLoading(false); }
  };

  const loadSavedCoupon = async () => {
    try {
      const r = await api.get('/billing/coupons/saved');
      setSavedCoupon(r.data.saved_coupon || null);
    } catch (_) { /* silent */ }
    finally { setSavedCouponLoaded(true); }
  };

  useEffect(() => { load(); loadSavedCoupon(); }, []);

  const showCoins = status?.kind === 'startup' && isEnabled('startup.coins');
  useEffect(() => {
    if (!showCoins) return;
    api.get('/coins/wallet').then((r) => setCoinWallet(r.data)).catch(() => setCoinWallet(null));
  }, [showCoins]);

  if (loading) return <div className="p-8 text-text-muted">Loading billing...</div>;
  if (!status) return null;

  const isStartup = status.kind === 'startup';
  const accent = isStartup ? 'success' : 'primary';
  const trialPct = Math.max(0, Math.min(100, (status.trial_days_left / status.trial_total_days) * 100));
  const isUrgent = status.trial_days_left <= 1 && status.trial_days_left > 0;
  const isExpired = !status.in_trial && !status.card;

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      {!hideHeader && <div className="startup-page-header mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <StartupPageTitle icon={PageHeadingIcon} className="text-3xl font-heading font-bold text-text-primary">Billing</StartupPageTitle>
          <p className="text-text-secondary text-sm mt-1">Trial countdown, plan{showCoins ? ', coins' : ''} and card on file for <strong>{status.fund_name}</strong>.</p>
        </div>
        {showCoins && <CoinsBalanceChip wallet={coinWallet} />}
      </div>}

      {/* VCs + incubators bill on a negotiated contract, not seats + card. */}
      {!isStartup && <FundContractBilling status={status} onChanged={load} />}

      {isStartup && <>
      {/* Trial countdown card */}
      <div className={`relative overflow-hidden rounded-xl border ${isExpired ? 'border-danger/40 bg-danger/5' : isUrgent ? 'border-warning/40 bg-warning/5' : `border-${accent}/30 bg-${accent}/5`} p-7 mb-8`} data-testid="trial-card">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${isExpired ? 'bg-danger/15' : isUrgent ? 'bg-warning/15' : `bg-${accent}/15`} flex items-center justify-center`}>
              {isExpired ? <Warning size={18} weight="fill" className="text-danger" />
                : isUrgent ? <Clock size={18} weight="fill" className="text-warning" />
                : <Sparkle size={18} weight="fill" className={`text-${accent}`} />}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">{isExpired ? 'Trial expired' : 'Free trial'}</div>
              <div className="text-2xl font-heading font-black text-text-primary">
                {isExpired ? 'Add a card to continue' : (
                  <>
                    {status.trial_days_left_int >= 1
                      ? <>{status.trial_days_left_int} day{status.trial_days_left_int === 1 ? '' : 's'} left</>
                      : <>{status.trial_hours_left} hour{status.trial_hours_left === 1 ? '' : 's'} left</>}
                  </>
                )}
              </div>
              <div className="text-xs text-text-secondary mt-0.5">
                {isExpired
                  ? 'Add a card on file to keep your team active'
                  : `${status.trial_total_days}-day free trial · started ${formatDate(status.trial_started_at)}`}
              </div>
            </div>
          </div>
          {!isExpired && (
            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${isUrgent ? 'bg-warning/15 text-warning' : `bg-${accent}/15 text-${accent}`}`}>
              {Math.round(trialPct)}% remaining
            </span>
          )}
        </div>
        <div className="h-2 rounded-full bg-text-muted/10 overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${isExpired ? 'bg-danger' : isUrgent ? 'bg-warning' : `bg-${accent}`}`} style={{ width: `${trialPct}%` }} />
        </div>
      </div>

      {/* Plan summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="border border-border rounded-lg p-5 bg-surface">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted mb-2">Tier</div>
          <div className={`text-2xl font-heading font-black text-${accent}`}>{status.current_tier?.label}</div>
          <div className="text-xs text-text-secondary mt-1">{format(status.current_tier?.price_per_seat, 'INR')} / seat</div>
        </div>
        <div className="border border-border rounded-lg p-5 bg-surface">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted mb-2">Active seats</div>
          <div className="text-3xl font-heading font-black text-text-primary tabular-nums">{status.seats}</div>
          <div className="text-xs text-text-secondary mt-1">Manage in Team tab</div>
        </div>
        <div className="border border-border rounded-lg p-5 bg-surface">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted mb-2">Monthly subtotal</div>
          <div className="text-3xl font-heading font-black text-text-primary tabular-nums">{format(status.monthly_estimate, 'INR')}</div>
          <div className="text-xs text-text-secondary mt-1">+ {status.gst_pct}% GST</div>
        </div>
      </div>

      {/* Card on file */}
      <div className="border border-border rounded-lg bg-surface p-6 mb-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-bold text-lg text-text-primary">Card on file</h2>
          {!status.card && !showCardForm && (
            <button onClick={() => setShowCardForm(true)} data-testid="add-card-btn"
              className={`px-4 py-2 bg-${accent} text-white rounded-lg text-sm font-semibold hover:opacity-90 flex items-center gap-2`}>
              <CreditCard size={16} weight="bold" /> Add card
            </button>
          )}
        </div>

        {status.card ? (
          <CardOnFile card={status.card} onRemoved={load} />
        ) : showCardForm ? (
          <CardForm onSaved={() => { setShowCardForm(false); load(); }} onCancel={() => setShowCardForm(false)} accent={accent} />
        ) : (
          <div className="text-center py-8">
            <CreditCard size={32} className="text-text-muted mx-auto mb-3" />
            <p className="text-sm text-text-secondary">No card on file. Razorpay-powered billing rolls out shortly — for now you can save card details to be ready when it goes live.</p>
          </div>
        )}
      </div>

      <div className="text-xs text-text-muted">
        Need a custom invoice or PO billing? <a href="mailto:hello@founderboard.in" className="underline hover:text-text-primary">Contact us</a>.
      </div>
      </>}

      {showCoins && <CoinsWallet wallet={coinWallet} />}

      <CouponSection accent={accent} isStartup={isStartup} saved={savedCoupon} setSaved={setSavedCoupon} loaded={savedCouponLoaded} />

      {isStartup && <MarketplaceCharges accent={accent} saved={savedCoupon} setSaved={setSavedCoupon} />}

      {isStartup && <ServicesInvoices accent={accent} />}
    </div>
  );
};

// ─────────────── Incubator / VC contract (vc_billing.py) ───────────────
const SUPPORT_EMAIL = 'hello@founderboard.in';
const fmtDay = (iso) => (iso
  ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
  : '—');
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const FundContractBilling = ({ status, onChanged }) => {
  const { format } = useCurrency();
  const c = status.contract || { enabled: false, state: 'self_serve' };

  if (!c.enabled) {
    const trialOver = !status.in_trial;
    return (
      <div className={`rounded-xl border ${trialOver ? 'border-danger/40 bg-danger/5' : 'border-primary/30 bg-primary/5'} p-7 mb-8`} data-testid="fund-billing-self-serve">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full ${trialOver ? 'bg-danger/15' : 'bg-primary/15'} flex items-center justify-center flex-shrink-0`}>
            {trialOver ? <Warning size={18} weight="fill" className="text-danger" /> : <Sparkle size={18} weight="fill" className="text-primary" />}
          </div>
          <div>
            <div className="text-2xl font-heading font-black text-text-primary">Contact support for billing details</div>
            <div className="text-sm text-text-secondary mt-1" data-testid="fund-billing-trial-line">
              {trialOver
                ? `Your ${status.trial_total_days}-day free trial has ended.`
                : <>You have a {status.trial_total_days}-day free trial — <strong>{status.trial_days_left_int >= 1 ? plural(status.trial_days_left_int, 'day') : plural(status.trial_hours_left, 'hour')} left</strong>.</>}
            </div>
            <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Billing — ${status.fund_name || ''}`)}`}
              data-testid="fund-billing-contact"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:opacity-90">
              Contact support
            </a>
          </div>
        </div>
      </div>
    );
  }

  const tone = {
    anchor: { box: 'border-primary/30 bg-primary/5', icon: <Sparkle size={18} weight="fill" className="text-primary" />, kicker: 'Anchor partner',
      headline: 'Complimentary access', line: 'Founderboard is free for your workspace — there is nothing to pay.' },
    free_period: { box: 'border-primary/30 bg-primary/5', icon: <Sparkle size={18} weight="fill" className="text-primary" />, kicker: 'Free period',
      headline: `Free until ${fmtDay(c.free_until)}`, line: `Your contract runs until ${fmtDay(c.expiry_date)}.` },
    active: c.due_soon
      ? { box: 'border-warning/40 bg-warning/5', icon: <Clock size={18} weight="fill" className="text-warning" />, kicker: 'Renewal due',
          headline: c.days_to_expiry <= 0 ? 'Renewal due today' : `Renewal due in ${plural(c.days_to_expiry, 'day')}`,
          line: `Your contract expires on ${fmtDay(c.expiry_date)}. Contact us to renew.` }
      : { box: 'border-success/30 bg-success/5', icon: <CheckCircle size={18} weight="fill" className="text-success" />, kicker: 'Active',
          headline: 'Subscription active', line: `Your contract runs until ${fmtDay(c.expiry_date)}.` },
    payment_due: { box: 'border-warning/40 bg-warning/5', icon: <Clock size={18} weight="fill" className="text-warning" />, kicker: 'Payment due',
      headline: 'Subscription payment due',
      line: `Pay for your ${fmtDay(c.agreement_date)} – ${fmtDay(c.expiry_date)} contract by ${fmtDay(c.payment?.pause_date)}, or your workspace will be paused.` },
    grace: { box: 'border-danger/40 bg-danger/5', icon: <Warning size={18} weight="fill" className="text-danger" />, kicker: 'Contract expired',
      headline: `Your subscription will be paused on ${fmtDay(c.pause_date)}`,
      line: `Your contract expired on ${fmtDay(c.expiry_date)}. Contact us to renew and keep your workspace active.` },
    paused: { box: 'border-danger/40 bg-danger/5', icon: <Warning size={18} weight="fill" className="text-danger" />, kicker: 'Paused',
      headline: 'Your subscription is paused',
      line: c.pause_reason === 'unpaid'
        ? `Payment for your ${fmtDay(c.agreement_date)} – ${fmtDay(c.expiry_date)} contract was due by ${fmtDay(c.payment?.pause_date)}. Your data is safe — pay below to restore access.`
        : `Your contract expired on ${fmtDay(c.expiry_date)}. Your data is safe — contact us to renew and restore access.` },
  }[c.state] || {};

  const rows = [
    ['Date of agreement', fmtDay(c.agreement_date)],
    ['Contract period', c.contract_months ? plural(c.contract_months, 'month') : '—'],
    ['Free period', c.free_unlimited ? 'Unlimited' : (c.free_days ? `${plural(c.free_days, 'day')} · until ${fmtDay(c.free_until)}` : 'None')],
    ['Contract expires', fmtDay(c.expiry_date)],
  ];

  return (
    <>
      <div className={`rounded-xl border ${tone.box} p-7 mb-8`} data-testid="fund-billing-contract" data-state={c.state}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-background/60 flex items-center justify-center flex-shrink-0">{tone.icon}</div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">{tone.kicker}</div>
            <div className="text-2xl font-heading font-black text-text-primary">{tone.headline}</div>
            <div className="text-sm text-text-secondary mt-1">{tone.line}</div>
            {c.state !== 'anchor' && (
              <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Renewal — ${status.fund_name || ''}`)}`}
                data-testid="fund-billing-contact"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:opacity-90">
                Contact support
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="border border-border rounded-lg p-5 bg-surface" data-testid="fund-billing-terms">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted mb-3">Contract</div>
          <dl className="space-y-2 text-sm">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <dt className="text-text-secondary">{k}</dt>
                <dd className="text-text-primary font-medium text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="border border-border rounded-lg p-5 bg-surface" data-testid="fund-billing-pricing">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted mb-3">Pricing</div>
          {c.free_unlimited || !c.price_pre_gst ? (
            <div className="text-3xl font-heading font-black text-text-primary">{format(0, 'INR')}</div>
          ) : (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-text-secondary">Contract price</dt><dd className="font-mono text-text-primary">{format(c.price_pre_gst, 'INR')}</dd></div>
              <div className="flex justify-between"><dt className="text-text-secondary">GST ({c.gst_pct}%)</dt><dd className="font-mono text-text-primary">{format(c.gst_amount, 'INR')}</dd></div>
              <div className="flex justify-between border-t border-border pt-2"><dt className="font-semibold text-text-primary">Total</dt><dd className="font-mono font-bold text-text-primary">{format(c.total_with_gst, 'INR')}</dd></div>
            </dl>
          )}
        </div>
      </div>

      {c.payment && c.payment.status !== 'none' && (
        <SubscriptionPayment payment={c.payment} onChanged={onChanged} />
      )}
      <SubscriptionInvoices refreshKey={c.payment?.invoice_number} />
    </>
  );
};

// Pay the current contract term through Cashfree. The backend verifies the
// order with Cashfree before marking the term paid and issuing the invoice.
const SubscriptionPayment = ({ payment, onChanged }) => {
  const { format } = useCurrency();
  const [form, setForm] = useState({ phone: '', gst_number: '', address: '' });
  const [paying, setPaying] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const ORDER_URL = '/billing/subscription/cashfree/order';
  const VERIFY_URL = '/billing/subscription/cashfree/verify';

  const settle = (result) => {
    if (!result.paid) { toast.error(cashfreeNotPaidMessage(result.status)); return; }
    toast.success(result.invoice?.number
      ? `Paid — invoice ${result.invoice.number} issued.`
      : (result.settledWithoutCheckout ? 'This term is already paid.' : 'Payment successful.'));
    onChanged();
  };

  // Cashfree's return_url lands back here if checkout left the modal.
  useEffect(() => {
    verifyCashfreeReturn(VERIFY_URL)
      .then((result) => result && settle(result))
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not verify payment'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async (e) => {
    e.preventDefault();
    setPaying(true);
    try {
      settle(await payWithCashfree({ orderUrl: ORDER_URL, verifyUrl: VERIFY_URL, body: form }));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Payment failed');
    } finally { setPaying(false); }
  };

  const paid = payment.status === 'paid';
  return (
    <div className={`rounded-xl border ${paid ? 'border-success/30 bg-success/5' : 'border-border bg-surface'} p-6 mb-8`}
      data-testid="subscription-payment" data-status={payment.status}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">Subscription payment</div>
          {paid ? (
            <>
              <div className="flex items-center gap-2 mt-1">
                <CheckCircle size={18} weight="fill" className="text-success" />
                <span className="text-xl font-heading font-black text-text-primary">Paid</span>
              </div>
              <div className="text-sm text-text-secondary mt-1" data-testid="subscription-invoice-ref">
                {format(payment.amount, 'INR')} on {formatDate(payment.paid_at)} · Invoice <strong className="font-mono text-text-primary">{payment.invoice_number || 'being issued'}</strong>
              </div>
            </>
          ) : (
            <>
              <div className="text-xl font-heading font-black text-text-primary mt-1">
                {format(payment.amount, 'INR')} <span className="text-sm font-medium text-text-secondary">incl. GST</span>
              </div>
              <div className="text-sm text-text-secondary mt-1">
                {payment.status === 'not_due'
                  ? `Due from ${fmtDay(payment.due_from)} — you can pay early.`
                  : 'Payment due for the current contract term.'}
              </div>
            </>
          )}
        </div>
        {!paid && !showForm && (
          <button onClick={() => setShowForm(true)} data-testid="subscription-pay-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:opacity-90">
            <CreditCard size={16} weight="bold" /> Pay via Cashfree
          </button>
        )}
      </div>
      {!paid && showForm && (
        <form onSubmit={pay} className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5" data-testid="subscription-pay-form">
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">Phone (for Cashfree)</label>
            <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="98765 43210"
              data-testid="subscription-phone" inputMode="tel"
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">GSTIN (optional, for the invoice)</label>
            <input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })} placeholder="29ABCDE1234F1Z5"
              data-testid="subscription-gstin" maxLength={15}
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm font-mono text-text-primary focus:outline-none focus:border-primary" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">Billing address (optional)</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Registered address for the invoice"
              data-testid="subscription-address"
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <CashfreePayButton type="submit" paying={paying} testid="subscription-pay-submit"
              className="flex-1 bg-primary" amountLabel={format(payment.amount, 'INR')} />
            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 bg-background border border-border text-text-primary rounded-lg text-sm">
              Cancel
            </button>
          </div>
          <p className="md:col-span-2 text-[11px] text-text-muted">Secure checkout is powered by Cashfree. Founderboard verifies the payment before issuing your invoice.</p>
        </form>
      )}
    </div>
  );
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 30000);
};

const SubscriptionInvoices = ({ refreshKey }) => {
  const { format } = useCurrency();
  const [invoices, setInvoices] = useState(null);
  const [downloading, setDownloading] = useState('');
  useEffect(() => {
    api.get('/billing/subscription/invoices').then((r) => setInvoices(r.data.invoices || [])).catch(() => setInvoices([]));
  }, [refreshKey]);
  const download = async (inv) => {
    setDownloading(inv.id);
    try {
      const r = await api.get(`/billing/subscription/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      downloadBlob(r.data, `${inv.number}.pdf`);
    } catch (e) { toast.error('Invoice not available'); }
    finally { setDownloading(''); }
  };
  if (!invoices || invoices.length === 0) return null;
  return (
    <div className="border border-border rounded-lg bg-surface p-6 mb-8" data-testid="subscription-invoices">
      <div className="flex items-center gap-2 mb-4">
        <Receipt size={16} weight="bold" className="text-primary" />
        <h2 className="font-heading font-bold text-lg text-text-primary">Subscription invoices</h2>
      </div>
      <div className="space-y-2">
        {invoices.map((inv) => (
          <div key={inv.id} className="bg-background rounded-lg border border-border p-3 flex items-center gap-3 flex-wrap" data-testid={`subscription-invoice-${inv.id}`}>
            <FileText size={14} weight="bold" className="text-text-muted flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-text-primary font-mono">{inv.number}</div>
              <div className="text-[11px] text-text-muted truncate">
                {fmtDay(inv.period_start)} – {fmtDay(inv.period_end)} · paid {formatDate(inv.paid_at)}
              </div>
            </div>
            <div className="font-mono font-bold text-sm text-text-primary">{format(inv.total, 'INR')}</div>
            <button onClick={() => download(inv)} disabled={downloading === inv.id} data-testid={`subscription-invoice-download-${inv.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white bg-primary disabled:opacity-50">
              <DownloadSimple size={12} weight="bold" /> {downloading === inv.id ? 'Preparing…' : 'PDF'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────── Coupon section (all user types) ───────────────
const CouponSection = ({ accent, isStartup, saved, setSaved, loaded }) => {
  const { format } = useCurrency();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const apply = async (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!c) return;
    setBusy(true);
    try {
      const r = await api.post('/billing/coupons/save', { code: c });
      setSaved(r.data.saved_coupon);
      setCode('');
      toast.success(`Coupon ${c} applied`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid coupon');
    } finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try {
      await api.delete('/billing/coupons/save');
      setSaved(null);
      toast.success('Coupon removed');
    } catch (_) { toast.error('Failed to remove'); }
    finally { setBusy(false); }
  };
  if (!loaded) return null;
  return (
    <div className="bg-surface border border-border rounded-2xl p-6 my-5" data-testid="coupon-section">
      <div className="flex items-center gap-2 mb-3">
        <Tag size={16} weight="bold" className={`text-${accent}`} />
        <h2 className="font-heading font-bold text-lg text-text-primary">Coupon</h2>
      </div>
      {saved ? (
        <div className={`flex items-center gap-3 rounded-lg border border-${accent}/30 bg-${accent}/5 px-4 py-3`} data-testid="coupon-saved">
          <CheckCircle size={16} weight="fill" className={`text-${accent} flex-shrink-0`} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-text-primary tracking-wide">{saved.code}</div>
            <div className="text-[11px] text-text-muted">
              {saved.kind === 'percent' ? `${saved.value}% off` : `${format(saved.value, 'INR')} off`}
              {' — '}
              {isStartup
                ? 'auto-applied to your next marketplace payment'
                : 'saved for your next subscription payment'}
            </div>
            {saved.description && <div className="text-[10px] text-text-muted italic mt-0.5">{saved.description}</div>}
          </div>
          <button
            onClick={clear}
            disabled={busy}
            data-testid="coupon-remove"
            className="text-text-muted hover:text-danger p-1"
            aria-label="Remove coupon"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      ) : (
        <form onSubmit={apply} className="flex gap-2" data-testid="coupon-form">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Enter coupon code"
            data-testid="coupon-input"
            autoComplete="off"
            spellCheck={false}
            className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono tracking-widest text-text-primary focus:outline-none focus:border-text-primary"
          />
          <button
            type="submit"
            disabled={busy || !code.trim()}
            data-testid="coupon-apply"
            className={`text-sm font-semibold px-4 py-2 rounded-lg text-white bg-${accent} disabled:opacity-50`}
          >
            {busy ? '...' : 'Apply'}
          </button>
        </form>
      )}
      <p className="text-[11px] text-text-muted mt-2">
        {isStartup
          ? 'Applies automatically to your next marketplace charge (Services & Marketing).'
          : 'Held on your account and applied when Razorpay subscription billing goes live.'}
      </p>
    </div>
  );
};

// (Iter 49) Outstanding + paid charges from marketplace engagements (Services upfront, Marketing monthly).
const MarketplaceCharges = ({ accent, saved, setSaved }) => {
  const { format } = useCurrency();
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState('');
  const load = async () => {
    try {
      const r = await api.get('/billing/charges');
      setData(r.data);
    } catch (e) { /* non-fatal */ }
  };
  useEffect(() => { load(); }, []);
  const discountFor = (amount) => {
    if (!saved) return 0;
    if (saved.kind === 'percent') return Math.round((amount * saved.value) / 100 * 100) / 100;
    return Math.min(saved.value, amount);
  };
  const pay = async (c) => {
    // Guided Services payments are completed inside the request wizard so the
    // Cashfree order is tied to the approved quote and invoice.
    if (c.vendor_type === 'service') {
      window.location.href = `/startup/services/request/${c.engagement_id}`;
      return;
    }
    setPaying(c.id);
    try {
      const body = saved ? { code: saved.code } : {};
      const r = await api.post(`/billing/charges/${c.id}/mark-paid`, body);
      if (r.data.applied_coupon) {
        toast.success(`Paid ${format(r.data.applied_coupon.final_amount, 'INR')} — coupon ${saved.code} saved you ${format(r.data.applied_coupon.discount, 'INR')}`);
        // Coupon consumed on this charge — clear the shared state (backend also cleared it via DELETE).
        try { await api.delete('/billing/coupons/save'); } catch (_) { /* non-fatal */ }
        setSaved(null);
      } else {
        toast.success('Payment recorded — engagement unlocked');
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); }
    finally { setPaying(''); }
  };
  if (!data) return null;
  const pending = data.charges.filter((c) => c.status === 'pending');
  const paid    = data.charges.filter((c) => c.status === 'paid');
  if (!pending.length && !paid.length) return null;
  return (
    <div className="bg-surface border border-border rounded-2xl p-8 my-5" data-testid="marketplace-charges">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-lg text-text-primary">Marketplace charges</h2>
        <div className="text-xs text-text-muted">
          <span className="text-warning font-semibold">{format(data.pending_total || 0, 'INR')} due</span> &middot; {format(data.paid_total || 0, 'INR')} paid
        </div>
      </div>
      {pending.length === 0 ? (
        <p className="text-sm text-text-muted">No outstanding charges — every active engagement is paid.</p>
      ) : (
        <div className="space-y-2 mb-3">
          {pending.map((c) => {
            const disc = discountFor(Number(c.amount));
            const finalAmt = Math.max(0, Number(c.amount) - disc);
            return (
            <div key={c.id} className="bg-background rounded-lg border border-warning/30 p-3 flex items-center gap-3 flex-wrap" data-testid={`billing-charge-${c.id}`}>
              <Warning size={14} weight="fill" className="text-warning flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text-primary truncate">{c.vendor_name}</div>
                <div className="text-[11px] text-text-muted truncate">{c.label}</div>
                {disc > 0 && (
                  <div className={`text-[10px] font-semibold text-${accent} mt-0.5`} data-testid={`billing-charge-discount-${c.id}`}>
                    Coupon {saved.code}: &minus;{format(disc, c.currency)}
                  </div>
                )}
              </div>
              <div className="text-right">
                {disc > 0 ? (
                  <>
                    <div className="font-mono text-[10px] text-text-muted line-through">{format(c.amount, c.currency)}</div>
                    <div className="font-mono font-bold text-sm text-text-primary">{format(finalAmt, c.currency)}</div>
                  </>
                ) : (
                  <div className="font-mono font-bold text-sm">{format(c.amount, c.currency)}</div>
                )}
              </div>
              <button onClick={() => pay(c)} disabled={paying === c.id}
                data-testid={`billing-pay-${c.id}`}
                className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white bg-${accent}`}>
                {c.vendor_type === 'service' ? 'Pay with Cashfree' : (paying === c.id ? 'Processing…' : 'Pay')}
              </button>
            </div>
            );
          })}
        </div>
      )}
      {paid.length > 0 && (
        <details className="mt-2">
          <summary className="text-[11px] uppercase tracking-wider font-bold text-text-muted cursor-pointer">{paid.length} paid charge{paid.length === 1 ? '' : 's'}</summary>
          <div className="mt-2 space-y-1">
            {paid.map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-[11px] text-text-muted py-1">
                <CheckCircle size={11} weight="fill" className="text-success" />
                <span className="flex-1 truncate">{c.vendor_name} &middot; {c.label}</span>
                <span className="font-mono">{format(c.amount, c.currency)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

// (Iter 68.2) Receipts for every managed service request that has been paid —
// the numbered invoice issued when the partner was cleared to start.
const ServicesInvoices = ({ accent }) => {
  const { format } = useCurrency();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState('');
  const load = async () => {
    try { const r = await api.get('/billing/invoices'); setData(r.data); } catch (_) { /* non-fatal */ }
  };
  useEffect(() => { load(); }, []);
  const download = async (inv) => {
    setDownloading(inv.id);
    try {
      const r = await api.get(`/service-requests/${inv.engagement_id}/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Invoice not available yet'); }
    finally { setDownloading(''); }
  };
  const invoices = data?.invoices || [];
  return (
    <div className="bg-surface border border-border rounded-2xl p-8 my-5" data-testid="services-invoices">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Receipt size={16} weight="bold" className={`text-${accent}`} />
          <h2 className="font-heading font-bold text-lg text-text-primary">Services Invoices</h2>
        </div>
        {invoices.length > 0 && (
          <div className="text-xs text-text-muted">
            {invoices.length} invoice{invoices.length === 1 ? '' : 's'} &middot; {format(data.total_paid || 0, 'INR')} paid
          </div>
        )}
      </div>
      {!data ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-text-muted" data-testid="services-invoices-empty">
          No service invoices yet. Once you pay for a managed service, its receipt appears here.
        </p>
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <div key={inv.id} className="bg-background rounded-lg border border-border p-3 flex items-center gap-3 flex-wrap"
              data-testid={`service-invoice-${inv.id}`}>
              <FileText size={14} weight="bold" className="text-text-muted flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text-primary truncate">
                  {inv.line_items?.[0]?.label || inv.bill_to?.name || 'Service invoice'}
                </div>
                <div className="text-[11px] text-text-muted truncate">
                  {inv.number} &middot; {inv.vendor_name || 'Founderboard'}
                  {inv.issued_at && <> &middot; {formatDate(inv.issued_at)}</>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-sm text-text-primary">{format(inv.total || 0, inv.currency || 'INR')}</div>
                {(inv.govt_fees || 0) > 0 && (
                  <div className="text-[10px] text-text-muted">incl. {format(inv.govt_fees, inv.currency || 'INR')} govt</div>
                )}
              </div>
              <button onClick={() => download(inv)} disabled={downloading === inv.id}
                data-testid={`service-invoice-download-${inv.id}`}
                className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white bg-${accent} disabled:opacity-50`}>
                <DownloadSimple size={12} weight="bold" /> {downloading === inv.id ? 'Preparing…' : 'PDF'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CardOnFile = ({ card, onRemoved }) => {
  const remove = async () => {
    if (!window.confirm('Remove this card?')) return;
    try {
      await api.delete('/billing/card');
      toast.success('Card removed');
      onRemoved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="flex items-center gap-5 p-5 bg-background rounded-lg border border-border" data-testid="card-on-file">
      <div className="w-14 h-9 bg-gradient-to-br from-text-primary to-text-secondary rounded flex items-center justify-center text-white font-mono text-[10px] font-bold">
        {(card.brand || 'CARD').toUpperCase()}
      </div>
      <div className="flex-1">
        <div className="font-mono text-sm text-text-primary tracking-widest">•••• •••• •••• {card.last4}</div>
        <div className="text-xs text-text-secondary mt-0.5">{card.holder_name} · expires {String(card.exp_month).padStart(2,'0')}/{String(card.exp_year).slice(-2)}</div>
      </div>
      <CheckCircle size={20} className="text-success" weight="fill" />
      <button onClick={remove} className="p-2 text-text-muted hover:text-danger" data-testid="remove-card-btn"><Trash size={16} weight="bold" /></button>
    </div>
  );
};

const CardForm = ({ onSaved, onCancel, accent }) => {
  const [form, setForm] = useState({ holder_name: '', card_number: '', exp_month: '', exp_year: '', cvc: '', brand: 'visa' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const digits = form.card_number.replace(/\s/g, '');
    if (digits.length < 13) { toast.error('Enter a valid card number'); return; }
    if (!form.holder_name.trim()) { toast.error('Enter cardholder name'); return; }
    const m = parseInt(form.exp_month, 10), y = parseInt(form.exp_year, 10);
    if (!m || m < 1 || m > 12 || !y) { toast.error('Enter a valid expiry'); return; }
    setSubmitting(true);
    try {
      await api.post('/billing/card', {
        holder_name: form.holder_name,
        last4: digits.slice(-4),
        brand: form.brand,
        exp_month: m,
        exp_year: y < 100 ? 2000 + y : y,
      });
      toast.success('Card saved');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const formatNumber = (v) => v.replace(/\D/g, '').slice(0, 19).replace(/(.{4})/g, '$1 ').trim();

  return (
    <div data-testid="card-form">
      <div className="text-xs text-text-muted mb-4">
        ⚠️ Stub form — only the last 4 digits are stored. Razorpay tokenisation will replace this when keys are configured.
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">Cardholder name</label>
          <input value={form.holder_name} onChange={e => setForm({ ...form, holder_name: e.target.value })} placeholder="Name on card"
            className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="card-holder-input" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">Card number</label>
          <input value={form.card_number} onChange={e => setForm({ ...form, card_number: formatNumber(e.target.value) })} placeholder="4242 4242 4242 4242"
            className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary font-mono focus:outline-none focus:border-primary" data-testid="card-number-input" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">Exp month</label>
            <input value={form.exp_month} onChange={e => setForm({ ...form, exp_month: e.target.value.replace(/\D/g, '').slice(0,2) })} placeholder="MM"
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="card-exp-month" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">Exp year</label>
            <input value={form.exp_year} onChange={e => setForm({ ...form, exp_year: e.target.value.replace(/\D/g, '').slice(0,4) })} placeholder="YYYY"
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="card-exp-year" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-1.5">CVC</label>
            <input value={form.cvc} onChange={e => setForm({ ...form, cvc: e.target.value.replace(/\D/g, '').slice(0,4) })} placeholder="123"
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="card-cvc" />
          </div>
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <button onClick={submit} disabled={submitting} className={`flex-1 py-3 bg-${accent} text-white rounded-lg font-semibold disabled:opacity-50`} data-testid="card-save-btn">
          {submitting ? 'Saving...' : 'Save card'}
        </button>
        <button onClick={onCancel} className="px-5 py-3 bg-background border border-border text-text-primary rounded-lg font-medium hover:border-primary">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default Billing;
